import { createContext, FC, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { noticeBaseUrl } from '../../../../baseUrl';
import { errorToast } from '../../../../CommonComponent/toast';
import { Notice } from '../types';
import useFetchWithAuth from '../../../../CommonHooks/useFetchWithAuth';
import { useMainContainerProvider } from '../../../../mainContainerProvider';
import { noop } from 'lodash';
import { asyncNoop } from '../../../../util';

interface NoticeContextType {
  noticeList: Notice[];
  setNoticesList: React.Dispatch<React.SetStateAction<Notice[]>>;
  templateList: Notice[];
  setTemplateList: React.Dispatch<React.SetStateAction<Notice[]>>;
  templateLoading: boolean;
  deliveredLoading: boolean;
  setTemplateLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setDeliveredLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleGetDeliveredList: () => Promise<void>;
  handleGetTemplateList: () => Promise<void>;
  setSelectedType: React.Dispatch<React.SetStateAction<'delivered' | 'template'>>;
  selectedType: 'delivered' | 'template';
}

const intitalValue: NoticeContextType = {
  noticeList: [],
  setNoticesList: noop,
  templateList: [],
  setTemplateList: noop,
  deliveredLoading: false,
  templateLoading: false,
  setTemplateLoading: noop,
  setDeliveredLoading: noop,
  handleGetDeliveredList: asyncNoop,
  handleGetTemplateList: asyncNoop,
  setSelectedType: noop,
  selectedType: 'delivered',
};
const NoticeContext = createContext<NoticeContextType>(intitalValue);
const NoticeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [noticeList, setNoticesList] = useState<Notice[]>([]);
  const [templateList, setTemplateList] = useState<Notice[]>([]);
  const [templateLoading, setTemplateLoading] = useState<boolean>(false);
  const [deliveredLoading, setDeliveredLoading] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<'delivered' | 'template'>('delivered');
  const { fetchWithAuth } = useFetchWithAuth();
  const { instituteId, uid } = useMainContainerProvider();

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleGetDeliveredList = async () => {
    setDeliveredLoading(true);
    const { error, data } = await fetchWithAuth(`notice-list/?uid=${uid}&noticeStatus=delivered&instituteId=${instituteId}&type=notice`, {}, noticeBaseUrl);
    if (!isMounted.current) return;
    setDeliveredLoading(false);
    if (error) {
      errorToast(error);
    } else {
      setNoticesList(data);
    }
  };
  const handleGetTemplateList = async () => {
    setTemplateLoading(true);
    const { error, data } = await fetchWithAuth(`notice-list/?uid=${uid}&instituteId=${instituteId}&noticeStatus=template&type=notice`, {}, noticeBaseUrl);
    if (!isMounted.current) return;
    setTemplateLoading(false);
    if (error) {
      errorToast(error);
    } else {
      setTemplateList(data);
    }
  };
  useEffect(() => {
    handleGetDeliveredList();
    handleGetTemplateList();
    // eslint-disable-next-line
  }, []);

  return (
    <NoticeContext.Provider
      value={{
        noticeList,
        setNoticesList,
        templateList,
        setTemplateList,
        setTemplateLoading,
        setDeliveredLoading,
        templateLoading,
        deliveredLoading,
        handleGetDeliveredList,
        handleGetTemplateList,
        setSelectedType,
        selectedType,
      }}
    >
      {children}
    </NoticeContext.Provider>
  );
};

export default NoticeProvider;

export const useNoticeProvider = () => useContext(NoticeContext);
